// أعلام الدول — **SVG محلّي لا رمز Emoji**.
//
// لماذا لا Emoji: ويندوز لا يحمل رموز الأعلام أصلًا (لا في Segoe UI Emoji ولا غيره)، فكروم
// يعرض «🇸🇦» حرفين لاتينيين في مربّعين لا علمًا. والمستخدم على ويندوز، فالرمز لم يكن ليظهر
// له أبدًا مهما كُتب في الكود. وهذه أشكال تُرسم في المتصفّح نفسه: تظهر في كل جهاز وكل
// متصفّح، وبلا ملفّ يُحمَّل ولا طلب شبكة ولا اعتمادية ثالثة (وهي قاعدة المشروع منذ الخطّ
// والشعار).
//
// وهي **أشكال مبسَّطة للتمييز** بحجم صغير: الألوان والتقسيم صحيحة، والشعارات الدقيقة
// (نصّ الشهادة في العلم السعودي · نسر مصر · عجلة أشوكا في علم الهند · ورقة القيقب الكندية)
// مرسومة رمزًا لا نقلًا حرفيًا — فالغرض أن يعرف البياع البلد بلمحة، لا أن يُطبع العلم.
import type { ReactElement } from 'react';

const W = 30, H = 20;

/** شرائط أفقية متساوية من أعلى لأسفل. */
function Bands({ colors }: { colors: string[] }): ReactElement {
  const h = H / colors.length;
  return <>{colors.map((c, i) => <rect key={i} y={i * h} width={W} height={h} fill={c} />)}</>;
}
/** شرائط رأسية متساوية (من اليسار بصريًا — العلم يُرسم بترتيبه الأصلي). */
function VBands({ colors }: { colors: string[] }): ReactElement {
  const w = W / colors.length;
  return <>{colors.map((c, i) => <rect key={i} x={i * w} width={w} height={H} fill={c} />)}</>;
}

const star = (cx: number, cy: number, r: number, fill: string) => {
  const pts = Array.from({ length: 10 }, (_, i) => {
    const rr = i % 2 === 0 ? r : r / 2.5;
    const a = (Math.PI / 5) * i - Math.PI / 2;
    return `${(cx + rr * Math.cos(a)).toFixed(2)},${(cy + rr * Math.sin(a)).toFixed(2)}`;
  }).join(' ');
  return <polygon points={pts} fill={fill} />;
};

const FLAGS: Record<string, ReactElement> = {
  EG: <><Bands colors={['#ce1126', '#fff', '#000']} /><circle cx="15" cy="10" r="2.4" fill="#c09300" /></>,
  SA: <><rect width={W} height={H} fill="#006c35" /><rect x="6" y="6.5" width="18" height="1.6" fill="#fff" rx="0.6" />
       <rect x="6" y="12" width="18" height="1.1" fill="#fff" rx="0.5" /><circle cx="6.6" cy="12.55" r="1.2" fill="#fff" /></>,
  AE: <><Bands colors={['#00732f', '#fff', '#000']} /><rect width="8" height={H} fill="#ce1126" /></>,
  KW: <><Bands colors={['#007a3d', '#fff', '#ce1126']} /><polygon points="0,0 9,6.7 9,13.3 0,20" fill="#000" /></>,
  QA: <><rect width={W} height={H} fill="#8d1b3d" /><polygon points="0,0 9,0 12,2.2 9,4.4 12,6.7 9,8.9 12,11.1 9,13.3 12,15.6 9,17.8 12,20 0,20" fill="#fff" /></>,
  BH: <><rect width={W} height={H} fill="#ce1126" /><polygon points="0,0 9,0 12,2.5 9,5 12,7.5 9,10 12,12.5 9,15 12,17.5 9,20 0,20" fill="#fff" /></>,
  OM: <><Bands colors={['#fff', '#ce1126', '#007a3d']} /><rect width="8" height={H} fill="#ce1126" /></>,
  JO: <><Bands colors={['#000', '#fff', '#007a3d']} /><polygon points="0,0 12,10 0,20" fill="#ce1126" />{star(4.2, 10, 1.8, '#fff')}</>,
  TR: <><rect width={W} height={H} fill="#e30a17" /><circle cx="12" cy="10" r="5" fill="#fff" /><circle cx="13.8" cy="10" r="4" fill="#e30a17" />{star(18.6, 10, 2.2, '#fff')}</>,
  GB: <><rect width={W} height={H} fill="#012169" />
       <path d="M0,0 L30,20 M30,0 L0,20" stroke="#fff" strokeWidth="4" /><path d="M0,0 L30,20 M30,0 L0,20" stroke="#c8102e" strokeWidth="2" />
       <path d="M15,0 V20 M0,10 H30" stroke="#fff" strokeWidth="6" /><path d="M15,0 V20 M0,10 H30" stroke="#c8102e" strokeWidth="3.4" /></>,
  FR: <VBands colors={['#002395', '#fff', '#ed2939']} />,
  DE: <Bands colors={['#000', '#dd0000', '#ffce00']} />,
  IT: <VBands colors={['#009246', '#fff', '#ce2b37']} />,
  ES: <><rect width={W} height={H} fill="#aa151b" /><rect y="5" width={W} height="10" fill="#f1bf00" /></>,
  NL: <Bands colors={['#ae1c28', '#fff', '#21468b']} />,
  GR: <><Bands colors={['#0d5eaf', '#fff', '#0d5eaf', '#fff', '#0d5eaf', '#fff', '#0d5eaf', '#fff', '#0d5eaf']} />
       <rect width="11.2" height="11.2" fill="#0d5eaf" /><path d="M5.6,0 V11.2 M0,5.6 H11.2" stroke="#fff" strokeWidth="2.2" /></>,
  RU: <Bands colors={['#fff', '#0039a6', '#d52b1e']} />,
  US: <><Bands colors={Array.from({ length: 13 }, (_, i) => (i % 2 === 0 ? '#b22234' : '#fff'))} />
       <rect width="13" height="10.8" fill="#3c3b6e" />
       {[0, 1, 2].map(r => [0, 1, 2, 3].map(c => <circle key={`${r}-${c}`} cx={2 + c * 3.2} cy={2 + r * 3.4} r="0.75" fill="#fff" />))}</>,
  JP: <><rect width={W} height={H} fill="#fff" /><circle cx="15" cy="10" r="5.5" fill="#bc002d" /></>,
  CN: <><rect width={W} height={H} fill="#de2910" />{star(6, 6, 3, '#ffde00')}
       {[[11.5, 2.6], [13.6, 4.6], [13.6, 7.6], [11.5, 9.6]].map(([x, y]) => star(x, y, 1.1, '#ffde00'))}</>,
  IN: <><Bands colors={['#ff9933', '#fff', '#138808']} /><circle cx="15" cy="10" r="2.6" fill="none" stroke="#000080" strokeWidth="0.8" /></>,
  MY: <><Bands colors={Array.from({ length: 14 }, (_, i) => (i % 2 === 0 ? '#cc0001' : '#fff'))} />
       <rect width="15" height="11.4" fill="#010066" /><circle cx="6.5" cy="5.7" r="3.4" fill="#ffcc00" /><circle cx="8" cy="5.7" r="2.8" fill="#010066" />{star(11.5, 5.7, 1.8, '#ffcc00')}</>,
  ID: <Bands colors={['#ce1126', '#fff']} />,
  CA: <><rect width={W} height={H} fill="#fff" /><rect width="7.5" height={H} fill="#d80621" /><rect x="22.5" width="7.5" height={H} fill="#d80621" />
       <polygon points="15,4 16.2,8 19,7 17.6,10.5 20,12 16.6,12.6 17,16 15,14.2 13,16 13.4,12.6 10,12 12.4,10.5 11,7 13.8,8" fill="#d80621" /></>,
  CH: <><rect width={W} height={H} fill="#d52b1e" /><rect x="12.5" y="4" width="5" height="12" fill="#fff" /><rect x="9" y="7.5" width="12" height="5" fill="#fff" /></>,
  DK: <><rect width={W} height={H} fill="#c8102e" /><rect x="9" width="3.4" height={H} fill="#fff" /><rect y="8.3" width={W} height="3.4" fill="#fff" /></>,
  NO: <><rect width={W} height={H} fill="#ba0c2f" /><rect x="8.2" width="5" height={H} fill="#fff" /><rect y="7.5" width={W} height="5" fill="#fff" /><rect x="9.6" width="2.2" height={H} fill="#00205b" /><rect y="8.9" width={W} height="2.2" fill="#00205b" /></>,
  SE: <><rect width={W} height={H} fill="#006aa7" /><rect x="9" width="3.4" height={H} fill="#fecc00" /><rect y="8.3" width={W} height="3.4" fill="#fecc00" /></>,
  EU: <><rect width={W} height={H} fill="#003399" />{Array.from({ length: 12 }, (_, i) => { const a = (Math.PI / 6) * i - Math.PI / 2; return star(15 + 6 * Math.cos(a), 10 + 6 * Math.sin(a), 0.9, '#ffcc00'); })}</>,
  AU: <><rect width={W} height={H} fill="#012169" /><rect width="15" height="10" fill="#012169" />
       <path d="M0,0 L15,10 M15,0 L0,10" stroke="#fff" strokeWidth="2" /><path d="M7.5,0 V10 M0,5 H15" stroke="#fff" strokeWidth="3" />
       <path d="M7.5,0 V10 M0,5 H15" stroke="#c8102e" strokeWidth="1.6" />
       {star(7.5, 15, 2, '#fff')}{star(22, 5, 1.6, '#fff')}{star(24, 12, 1.6, '#fff')}{star(20, 16, 1.2, '#fff')}</>,
};

/** علم الدولة بحجم السطر — و`alt` نصّي لقارئ الشاشة عبر `title`. */
export function Flag({ code, title }: { code: string; title: string }) {
  const art = FLAGS[code];
  if (!art) return null;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="24" height="16" role="img" aria-label={`علم ${title}`}
      className="inline-block shrink-0 rounded-[2px] ring-1 ring-black/10">
      {art}
    </svg>
  );
}
